import { useEffect, useState, useRef } from "react";
import { motion } from "motion/react";
import { colord } from "colord";
import { cn } from "@/lib/utils";

interface PillToggleProps {
  options: string[];
  defaultValue?: string;
  /** Controlled value — when provided, overrides internal state */
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  highlightColor?: string;
  textColor?: string;
}

export function PillToggle({
  options,
  defaultValue,
  value: controlledValue,
  onChange,
  className,
  highlightColor = "bg-background",
  textColor,
}: PillToggleProps) {
  const [internalSelected, setInternalSelected] = useState<string>(
    defaultValue || options[0] || "",
  );
  const selected = controlledValue ?? internalSelected;
  const [dimensions, setDimensions] = useState<
    Array<{ width: number; left: number }>
  >([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<(HTMLButtonElement | null)[]>([]);

  // Determine text color based on background luminance or use manual override
  const getTextColor = (bgColor: string): string => {
    // If textColor is manually provided, use it
    if (textColor) {
      return textColor;
    }

    // If it's a CSS variable in HSL format, we need to handle it specially
    if (bgColor.startsWith("hsl(var(")) {
      // For theme colors, we'll use a default approach
      // Primary is usually dark, so we'll use white text
      if (bgColor === "bg-primary" || bgColor === "bg-secondary") {
        return "text-primary-foreground";
      }
      // Background is usually light, so we'll use dark text
      return "text-foreground";
    }

    // For direct color values or mapped Tailwind colors
    try {
      const color = colord(bgColor);
      // Use WCAG recommended contrast ratio threshold
      return color.isLight() ? "text-gray-900" : "text-white";
    } catch (e) {
      // Fallback if color parsing fails
      return "text-foreground";
    }
  };

  const textColorCalculated = getTextColor(highlightColor);

  // Calculate dimensions of each item for the highlight pill
  useEffect(() => {
    if (containerRef.current && itemsRef.current.length > 0) {
      const containerLeft = containerRef.current.getBoundingClientRect().left;
      const newDimensions = itemsRef.current.map((item) => {
        if (!item) return { width: 0, left: 0 };
        const rect = item.getBoundingClientRect();
        return {
          width: rect.width,
          left: rect.left - containerLeft,
        };
      });
      setDimensions(newDimensions);
    }
  }, [options]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current && itemsRef.current.length > 0) {
        const containerLeft = containerRef.current.getBoundingClientRect().left;
        const newDimensions = itemsRef.current.map((item) => {
          if (!item) return { width: 0, left: 0 };
          const rect = item.getBoundingClientRect();
          return {
            width: rect.width,
            left: rect.left - containerLeft,
          };
        });
        setDimensions(newDimensions);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSelect = (value: string) => {
    setInternalSelected(value);
    onChange?.(value);
  };

  const selectedIndex = options.indexOf(selected);

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative inline-flex rounded-full bg-background p-1 text-muted-foreground",
        className,
      )}
    >
      {/* Animated highlight pill */}
      {selectedIndex !== -1 && dimensions[selectedIndex] && (
        <motion.div
          className={cn("absolute top-1 rounded-full", highlightColor)}
          initial={false}
          animate={{
            width: dimensions[selectedIndex]?.width,
            left: dimensions[selectedIndex]?.left,
            height: "calc(100% - 0.5rem)",
          }}
          transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
        />
      )}

      {/* Option buttons */}
      {options.map((option, index) => (
        <button
          key={option}
          ref={(el) => (itemsRef.current[index] = el)}
          onClick={() => handleSelect(option)}
          className={cn(
            "relative z-10 rounded-full flex-1 px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer",
            selected === option ? textColorCalculated : "hover:text-foreground",
          )}
        >
          {option}
        </button>
      ))}
    </div>
  );
}
